import type { ApiAbsence } from '@/api/data';
/**
 * Sykefraværsstatistikk in the official measure (owner, 2026-09-21): sick-leave days lost as a
 * percentage of agreed working days, SSB's and NAV's "tapte dagsverk i prosent av avtalte
 * dagsverk". Sick leave is the egenmelding and sykemelding categories; syk barn is care leave
 * and not in it.
 *
 * Avtalte dagsverk here: the people counted times the weekdays of the month, Monday to Friday.
 * Every person counts as full time, because the position percentage is stored nowhere; a
 * part-timer's sick day then weighs the same as anyone's, in the lost days and in the agreed
 * days alike. Tapte dagsverk: the weekdays of each approved absence inside the month, the end
 * date included (ruling 2). Pending and declined requests are not absence. A case is one
 * approved sick absence with at least one lost weekday in the month.
 */
import { T } from '@/i18n';
import { addDays, dow } from '@timefly/core';
import { CATEGORIES, type Category, catOf, isSickLeave } from './category';
import { spanOf } from './dates';

export const lastOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
};
export const shiftMonth = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const x = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, '0')}`;
};
export function weekdays(a: string, b: string) {
  let n = 0;
  for (let d = a; d <= b; d = addDays(d, 1)) if (dow(d) < 5) n++;
  return n;
}

export interface MonthStats {
  ym: string;
  agreed: number;
  /** lost weekdays by category */
  days: Record<Category, number>;
  cases: number;
  /** percent of agreed days: all sick leave, and its two parts */
  sick: number;
  egen: number;
  sykm: number;
}

export function monthStats(ym: string, absences: ApiAbsence[], people: ReadonlySet<number>): MonthStats {
  const first = `${ym}-01`;
  const last = lastOf(ym);
  const agreed = people.size * weekdays(first, last);
  const days = Object.fromEntries(CATEGORIES.map((c) => [c.key, 0])) as Record<Category, number>;
  let cases = 0;
  for (const a of absences) {
    if (a.status !== 'accepted' || !people.has(a.user_id)) continue;
    const { from, to } = spanOf(a);
    if (to < first || from > last) continue;
    const n = weekdays(from < first ? first : from, to > last ? last : to);
    const c = catOf(a.absence_type);
    days[c] += n;
    if (isSickLeave(c) && n > 0) cases++;
  }
  const pct = (n: number) => (agreed ? (n / agreed) * 100 : 0);
  return {
    ym,
    agreed,
    days,
    cases,
    sick: pct(days.egenmelding + days.sykemelding),
    egen: pct(days.egenmelding),
    sykm: pct(days.sykemelding),
  };
}

/** The six months ending with `ym`, oldest first. */
export const trendOf = (ym: string, absences: ApiAbsence[], people: ReadonlySet<number>, n = 6) =>
  Array.from({ length: n }, (_, i) => monthStats(shiftMonth(ym, i - n + 1), absences, people));

export interface Insight {
  icon: 'down' | 'up' | 'flat' | 'cases' | 'share';
  title: string;
  text: string;
}
const pp = (n: number) => `${n.toLocaleString('nb-NO', { maximumFractionDigits: 1 })}`;

/** Two or three plain sentences a manager can act on; nothing when there is nothing to compare. */
export function insightsOf(cur: MonthStats, prev: MonthStats): Insight[] {
  const out: Insight[] = [];
  const d = Math.round((cur.sick - prev.sick) * 10) / 10;
  if (d < 0)
    out.push({
      icon: 'down',
      title: T('Sykefraværet er lavere enn forrige måned'),
      text: T('Ned {d} prosentpoeng, fra {from} % til {to} %.', {
        d: pp(-d),
        from: pp(prev.sick),
        to: pp(cur.sick),
      }),
    });
  else if (d > 0)
    out.push({
      icon: 'up',
      title: T('Sykefraværet er høyere enn forrige måned'),
      text: T('Opp {d} prosentpoeng, fra {from} % til {to} %.', {
        d: pp(d),
        from: pp(prev.sick),
        to: pp(cur.sick),
      }),
    });
  else
    out.push({
      icon: 'flat',
      title: T('Sykefraværet er som forrige måned'),
      text: T('{p} % begge måneder.', { p: pp(cur.sick) }),
    });
  const c = cur.cases - prev.cases;
  if (c)
    out.push({
      icon: 'cases',
      title: c < 0 ? T('Færre fraværstilfeller') : T('Flere fraværstilfeller'),
      text:
        c < 0
          ? T('{n} færre tilfeller enn forrige måned ({cur} mot {prev}).', {
              n: String(-c),
              cur: String(cur.cases),
              prev: String(prev.cases),
            })
          : T('{n} flere tilfeller enn forrige måned ({cur} mot {prev}).', {
              n: String(c),
              cur: String(cur.cases),
              prev: String(prev.cases),
            }),
    });
  if (cur.sick > 0) {
    const sykm = cur.sykm >= cur.egen;
    const share = String(Math.round((Math.max(cur.sykm, cur.egen) / cur.sick) * 100));
    out.push({
      icon: 'share',
      title: sykm ? T('Sykemelding utgjør størst andel') : T('Egenmelding utgjør størst andel'),
      text: sykm
        ? T('{share} % av sykefraværet er sykemelding.', { share })
        : T('{share} % av sykefraværet er egenmelding.', { share }),
    });
  }
  return out;
}
